import { z } from 'zod';
import { safeProjectPath } from './project-artifact.js';
import { criterionLabelSchema, reviewDimensionSchema, workshopReviewSchema, workshopReviewV2Schema, } from './workshop-review.js';
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const slug = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/);
const solutionFilesSchema = z
    .array(z
    .object({
    path: z.string().max(256).refine(safeProjectPath),
    content: z.string().max(65_536),
})
    .strict())
    .min(1)
    // A full-stack reference (Python API plus a React web/ tree) exceeded the earlier 32, the
    // Module 3 model features (notes, migrations, a client, tests, a panel) exceed 64, and the
    // Module 4 assistant (tools, retrieval, conversations, approvals, evaluations) exceeds 96.
    .max(128);
/** Authored reference solution; served only after the attempt's submissions are exhausted or passed. */
export const workshopSolutionSchema = z
    .object({
    files: solutionFilesSchema,
    /** Markdown walkthrough of the key decisions, shown beside the files. */
    walkthrough: z.string().trim().min(1).max(12_000),
})
    .strict()
    .superRefine((solution, ctx) => {
    if (new Set(solution.files.map((file) => file.path)).size !== solution.files.length)
        ctx.addIssue({ code: 'custom', path: ['files'], message: 'Solution paths must be unique' });
    if (new TextEncoder().encode(JSON.stringify(solution)).byteLength > 1_000_000)
        ctx.addIssue({ code: 'custom', message: 'Reference solution exceeds the total size limit' });
});
export const workshopSpecSchema = z
    .object({
    version: z.literal(2),
    starterKey: z.string().regex(/^starters\/[a-zA-Z0-9/_.-]+$/),
    starterDigest: digest,
    mode: z.enum(['practice', 'unaided']).default('practice'),
    objectives: z.array(z.string().min(1).max(1000)).min(1).max(12),
    hints: z.array(z.string().min(1).max(2000)).min(1).max(4),
    rubric: z
        .array(z
        .object({
        id: slug,
        description: z.string().min(1).max(1000),
        /** Learner-safe title; the description stays private to the reviewer. */
        label: criterionLabelSchema.optional(),
        dimension: reviewDimensionSchema.nullable().default(null),
        required: z.boolean().default(true),
    })
        .strict())
        .min(1)
        .max(8),
    /** Overrides the environment default for how many reviewed submissions an attempt may use. */
    submissionLimit: z.number().int().min(1).max(10).optional(),
    solution: workshopSolutionSchema.optional(),
})
    .strict()
    .superRefine((spec, ctx) => {
    if (new Set(spec.rubric.map((criterion) => criterion.id)).size !== spec.rubric.length)
        ctx.addIssue({ code: 'custom', path: ['rubric'], message: 'Rubric IDs must be unique' });
    if (!spec.rubric.some((criterion) => criterion.required))
        ctx.addIssue({
            code: 'custom',
            path: ['rubric'],
            message: 'At least one required criterion is needed',
        });
});
/** Deliberately separate from the internal authored specification. */
export const workshopTaskSchema = z.object({
    id: z.uuid(),
    activityId: z.uuid(),
    revision: digest,
    title: z.string(),
    body: z.string(),
    acceptance: z.array(z.string()),
    objectives: z.array(z.string()),
    mode: z.enum(['practice', 'unaided']),
});
/** Owned work from a retired curriculum, separate from the active activity list. */
export const earlierWorkshopsSchema = z.object({
    items: z
        .array(z.object({
        activityId: z.uuid(),
        code: z.string(),
        title: z.string(),
        status: z.enum(['active', 'submitted', 'complete']),
    }))
        .max(50),
    nextCursor: z.uuid().nullable(),
});
export const workspaceStateSchema = z.enum([
    'allocated',
    'requested',
    'queued',
    'provisioning',
    'starting',
    'ready',
    'stopping',
    'stopped',
    'failed',
    'deleting',
    'deleted',
]);
export const attemptSchema = z.object({
    id: z.uuid(),
    activityId: z.uuid(),
    taskVersionId: z.uuid(),
    status: z.enum(['active', 'submitted', 'complete', 'abandoned']),
    assistanceLevel: z.number().int().min(0).max(4),
    createdAt: z.iso.datetime(),
});
export const workspaceSchema = z.object({
    id: z.uuid(),
    activeAttemptId: z.uuid().nullable(),
    state: workspaceStateSchema,
    generation: z.number().int(),
    failureCode: z.string().nullable(),
    updatedAt: z.iso.datetime(),
    /** When automatic idle shutdown would pause ready compute without new activity. */
    idleStopAt: z.iso.datetime().nullable().optional(),
    /** Field holds the shared compute; a Workshop activation must release Field first. */
    fieldActive: z.boolean().optional(),
});
/** Response of an explicit "keep working" touch. */
export const workspaceActivitySchema = z.object({ idleStopAt: z.iso.datetime().nullable() });
export const workshopSessionSchema = z.object({
    task: workshopTaskSchema.nullable(),
    attempt: attemptSchema.nullable(),
    workspace: workspaceSchema.nullable(),
    entitled: z.boolean(),
});
export const launchSchema = z.object({
    action: z.url(),
    grant: z.string(),
    expiresAt: z.iso.datetime(),
});
export const contextFileSchema = z.object({
    path: z.string().min(1).max(256),
    content: z.string().max(65536),
    documentVersion: z.number().int().nonnegative(),
    dirty: z.boolean(),
});
export const editorContextSchema = z
    .object({
    generation: z.number().int().positive(),
    capturedAt: z.iso.datetime(),
    files: z.array(contextFileSchema).max(5),
    selection: z
        .object({
        path: z.string().max(256),
        startLine: z.number().int().positive(),
        endLine: z.number().int().positive(),
    })
        .nullable(),
    diagnostics: z
        .array(z.object({
        path: z.string().max(256),
        line: z.number().int().positive(),
        message: z.string().max(1000),
    }))
        .max(30),
})
    .strict();
export const evaluationCriteriaSchema = z
    .array(z
    .object({
    id: slug,
    result: z.enum(['met', 'partial', 'not_met']),
    feedback: z.string().min(1).max(1000),
})
    .strict())
    .min(1)
    .max(8);
const legacyEvaluationSchema = z
    .object({
    verdict: z.enum(['passed', 'revision_requested']),
    criteria: evaluationCriteriaSchema,
    feedback: z.string().min(1).max(2000),
    knowledgeGaps: z
        .array(z
        .object({
        concept: z.string().min(1).max(200),
        evidence: z.string().min(1).max(1000),
    })
        .strict())
        .max(8),
    nextSteps: z.array(z.string().min(1).max(1000)).max(8),
})
    .strict()
    .superRefine((evaluation, ctx) => {
    if (new Set(evaluation.criteria.map((item) => item.id)).size !== evaluation.criteria.length)
        ctx.addIssue({ code: 'custom', path: ['criteria'], message: 'Criterion IDs must be unique' });
    if ((evaluation.verdict === 'passed') !==
        evaluation.criteria.every((item) => item.result === 'met'))
        ctx.addIssue({
            code: 'custom',
            path: ['verdict'],
            message: 'Verdict must agree with criteria',
        });
});
// Retained submissions remain readable after the evaluator is upgraded.
export const evaluationSchema = z.union([
    workshopReviewSchema,
    workshopReviewV2Schema,
    legacyEvaluationSchema,
]);
export const submissionSchema = z.object({
    id: z.uuid(),
    status: z.enum(['queued', 'evaluating', 'revision_requested', 'passed', 'failed']),
    artifactHash: digest,
    explanation: z.string(),
    evaluation: evaluationSchema.nullable(),
    evaluationVersion: z.string(),
    modelId: z.string(),
    errorCode: z.string().nullable(),
    evaluatedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    /** Server-computed: a saved review can be retried with the current evaluator. */
    retryable: z.boolean().optional(),
});
/** How many reviewed submissions this attempt may still use, and whether its reference is visible. */
export const submissionPolicySchema = z
    .object({
    limit: z.number().int().min(1),
    used: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(),
    locked: z.boolean(),
    lockReason: z.enum(['passed', 'limit_reached']).nullable(),
    solutionRevealed: z.boolean(),
    solutionAvailable: z.boolean(),
})
    .strict();
export const workshopHistorySchema = z.object({
    submissions: z.array(submissionSchema),
    nextCursor: z.uuid().nullable().default(null),
    policy: submissionPolicySchema.optional(),
});
export const workshopSolutionViewSchema = z.object({
    files: solutionFilesSchema,
    walkthrough: z.string(),
    /** `current` means the pinned task version had no solution and the activity's newest version supplied it. */
    source: z.enum(['pinned', 'current']),
    /** For `current`: whether that version's starter files are the ones this attempt received. */
    starterMatches: z.boolean().optional(),
});
export const retryWorkshopReviewSchema = z.object({ idempotencyKey: z.uuid() }).strict();
export const retriedWorkshopReviewSchema = z.object({ submissionId: z.uuid(), jobId: z.uuid() });
export const retryableWorkshopReviewErrors = new Set([
    'assessment_invalid_response',
    'assessment_timeout',
    'assessment_unavailable',
    'model_output_truncated',
    'model_output_incomplete',
    'model_output_empty',
    'job_failed',
    'job_interrupted',
    'retry_exhausted',
]);
export const mentorTurnRequestSchema = z
    .object({
    idempotencyKey: z.uuid(),
    activityId: z.uuid().nullable(),
    attemptId: z.uuid().nullable(),
    message: z.string().trim().min(1).max(8000),
})
    .strict();
export const mentorTurnSchema = z.object({
    id: z.uuid(),
    status: z.enum(['pending', 'running', 'succeeded', 'failed', 'cancelled']),
    content: z.string().nullable(),
    assistanceLevel: z.number().int().min(0).max(4),
    errorCode: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const mentorHistorySchema = z.object({
    messages: z.array(z.object({
        id: z.string(),
        role: z.enum(['learner', 'mio']),
        content: z.string(),
        createdAt: z.iso.datetime(),
    })),
});
export const memoryRecordSchema = z.object({
    id: z.uuid(),
    concept: z.string(),
    summary: z.string(),
    evidenceIds: z.array(z.uuid()),
    updatedAt: z.iso.datetime(),
});
export const memoryListSchema = z.object({ records: z.array(memoryRecordSchema) });
export const memoryCorrectionSchema = z
    .object({ summary: z.string().trim().min(1).max(2000) })
    .strict();
export const mentorPreferencesSchema = z.object({ quiet: z.boolean() }).strict();
export const proactiveOfferSchema = z.object({
    message: z.string().nullable(),
    offerId: z.uuid().nullable(),
});
export const okSchema = z.object({ ok: z.literal(true) });
